import { useCallback } from 'react';
import noop from 'lodash/noop';
import type { RouteContext } from '@atlassian/react-resource-router';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import {
	EntryKeys,
	ENTRYPOINT_ID_JIRA_PLATFORM,
} from '@atlassian/jira-feedback-collector/src/constants.tsx';
import normaliseUrl from '@atlassian/jira-platform-url-normalisation';

const unknownAttributeValue = 'unknown';

const FEEDBACK_GROUP_ID = 'workflow-editor-opt-out';

type AdditionalEntries = {
	isAdmin: boolean;
	routeName: string;
	projectType: string;
	cloudId: string;
};

export const getAdditionalFeedbackEntries = ({
	isAdmin,
	routeName,
	projectType,
	cloudId,
}: AdditionalEntries) => [
	{
		id: EntryKeys.LOCATION,
		value: normaliseUrl(globalThis.window?.location.href),
	},
	{
		id: EntryKeys.REFERER,

		value: globalThis.document?.referrer ? normaliseUrl(globalThis.document?.referrer) : '',
	},
	{
		id: EntryKeys.USER_AGENT,
		value: globalThis.navigator?.userAgent,
	},
	{
		id: EntryKeys.SCREEN_RESOLUTION,
		value: `${globalThis.window?.screen?.width} x ${globalThis.window?.screen?.height}`,
	},
	{
		id: EntryKeys.ROUTE_NAME,
		value: routeName,
	},
	{
		id: EntryKeys.IS_ADMIN,
		value: isAdmin.toString(),
	},
	{
		id: EntryKeys.PROJECT_TYPE,
		value: projectType,
	},
	{
		id: 'cloud-id',
		value: cloudId,
	},
];

export const useProvideFeedbackDialog = (routeContext: RouteContext) => {
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const cloudId = useCloudId();
	const isAdmin = useIsAdmin();

	const routeName = routeContext.route.name ?? unknownAttributeValue;

	const renderFeedbackDialog = useCallback(() => {
		openFeedbackCollector({
			entrypointId: ENTRYPOINT_ID_JIRA_PLATFORM,
			feedbackGroupId: FEEDBACK_GROUP_ID,
			onClose: noop,
			feedbackContext: getAdditionalFeedbackEntries({
				isAdmin,
				cloudId,
				routeName,
				projectType: unknownAttributeValue,
			}),
		});
	}, [openFeedbackCollector, isAdmin, cloudId, routeName]);

	return { renderFeedbackDialog };
};
